/**
 * CSV lead import — renders inside the resizable side pane (no popup).
 *
 *   const csv = useCSVImportPane({ open: showCSVImport, onOpenChange: setShowCSVImport, onImported });
 *   side = showCSVImport ? { open: true, onOpenChange: csv.close, title: csv.title, icon: csv.icon,
 *                            actions: csv.headerBadge, children: csv.body, footer: csv.footer, testid: 'csv-import-dialog' } : null;
 *
 * Same props as the former <CSVImportDialog>; `csv.close()` resets the wizard and calls onOpenChange(false).
 */
import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Available lead fields and their possible header aliases (for auto-detect)
const LEAD_FIELDS = [
  { key: 'first_name', label: 'First Name', required: true, aliases: ['firstname', 'first name', 'fname', 'given name'] },
  { key: 'last_name', label: 'Last Name', required: true, aliases: ['lastname', 'last name', 'lname', 'surname', 'family name'] },
  { key: 'email', label: 'Email', aliases: ['email', 'email address', 'e-mail', 'mail'] },
  { key: 'phone', label: 'Phone', aliases: ['phone', 'mobile', 'contact', 'phone number', 'mobile number', 'whatsapp'] },
  { key: 'guardian_name', label: 'Guardian Name', aliases: ['guardian', 'parent', 'parent name', 'guardian name'] },
  { key: 'student_level', label: 'Student Level', aliases: ['level', 'student level', 'category', 'type'] },
  { key: 'school_class', label: 'Class', aliases: ['class', 'grade', 'standard'] },
  { key: 'school_name', label: 'School Name', aliases: ['school', 'school name', 'institution'] },
  { key: 'school_board', label: 'Board', aliases: ['board'] },
  { key: 'college_year', label: 'College Year', aliases: ['college year', 'year'] },
  { key: 'college_name', label: 'College Name', aliases: ['college', 'college name'] },
  { key: 'professional_course', label: 'Professional Course', aliases: ['professional course', 'course'] },
  { key: 'professional_level', label: 'Professional Level', aliases: ['professional level'] },
  { key: 'competitive_exam', label: 'Competitive Exam', aliases: ['competitive exam', 'exam'] },
  { key: 'source', label: 'Source', aliases: ['source', 'lead source', 'channel'] },
  { key: 'notes', label: 'Notes', aliases: ['notes', 'note', 'comments', 'description', 'remarks'] },
];

// Simple CSV parser supporting quoted fields and basic escaping
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }
        else { inQuotes = false; }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { cur.push(field); field = ''; }
      else if (ch === '\r') { /* ignore */ }
      else if (ch === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; }
      else field += ch;
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows.filter(r => r.some(c => c && c.trim().length > 0));
}

function autoDetectMapping(headers) {
  const mapping = {};
  headers.forEach((h, idx) => {
    const norm = h.trim().toLowerCase().replace(/[^a-z0-9 ]/g, '');
    const match = LEAD_FIELDS.find(f =>
      f.key === norm.replace(/ /g, '_') ||
      f.aliases.some(a => a === norm || a.replace(/ /g, '') === norm.replace(/ /g, ''))
    );
    if (match) mapping[idx] = match.key;
  });
  return mapping;
}

export function useCSVImportPane({ open, onOpenChange, onImported } = {}) {
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | map | preview | done
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({}); // { columnIndex: leadFieldKey }
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep('upload'); setFileName(''); setHeaders([]); setRows([]);
    setMapping({}); setImporting(false); setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => { reset(); onOpenChange?.(false); };

  const handleFile = (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text);
        if (parsed.length < 2) {
          toast.error('CSV is empty or missing header row');
          return;
        }
        const hdr = parsed[0].map(h => h.trim());
        const body = parsed.slice(1);
        setHeaders(hdr);
        setRows(body);
        setMapping(autoDetectMapping(hdr));
        setStep('map');
      } catch {
        toast.error('Failed to parse CSV');
      }
    };
    reader.readAsText(file);
  };

  const updateMapping = (colIdx, leadKey) => {
    setMapping(prev => {
      const next = { ...prev };
      // Remove this leadKey from any other column (one-to-one)
      Object.keys(next).forEach(k => { if (next[k] === leadKey) delete next[k]; });
      if (leadKey === '__skip__') delete next[colIdx];
      else next[colIdx] = leadKey;
      return next;
    });
  };

  const buildLeads = () => {
    const mapped = Object.entries(mapping); // [colIdx, key]
    return rows.map(row => {
      const lead = { source: 'other' };
      mapped.forEach(([colIdx, key]) => {
        const val = (row[Number(colIdx)] || '').trim();
        if (val) lead[key] = val;
      });
      if (!lead.first_name && !lead.last_name) {
        // Try to use whatever name we have
        if (lead.email) lead.first_name = lead.email.split('@')[0];
        else lead.first_name = 'Unknown';
      }
      if (!lead.first_name) lead.first_name = '-';
      if (!lead.last_name) lead.last_name = '-';
      return lead;
    });
  };

  const validateMapping = () => {
    const mappedKeys = Object.values(mapping);
    if (!mappedKeys.includes('first_name') && !mappedKeys.includes('last_name')) {
      toast.error('Map at least First Name or Last Name');
      return false;
    }
    if (!mappedKeys.includes('email') && !mappedKeys.includes('phone')) {
      toast.error('Map at least Email or Phone');
      return false;
    }
    return true;
  };

  const handlePreview = () => {
    if (!validateMapping()) return;
    setStep('preview');
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const leads = buildLeads();
      if (leads.length > 500) {
        toast.error('Maximum 500 leads per import. Please split your file.');
        setImporting(false);
        return;
      }
      const res = await api.post('/crm/leads/bulk-import', { leads });
      setResult(res.data);
      setStep('done');
      onImported?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const previewLeads = step === 'preview' ? buildLeads().slice(0, 5) : [];
  const mappedColumns = Object.keys(mapping).length;
  const mappedFields = LEAD_FIELDS.filter(f => Object.values(mapping).includes(f.key));

  // ─── Body per step ───
  let body = null;
  let footer = null;

  if (step === 'upload') {
    body = (
      <div className="space-y-4">
        <Card className="border-dashed border-2 hover:bg-muted/20 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          <CardContent className="p-10 text-center">
            <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">Click to upload CSV file</p>
            <p className="text-xs text-muted-foreground mt-1">Max 500 leads per import</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="csv-file-input"
            />
          </CardContent>
        </Card>
        <div className="text-xs text-muted-foreground space-y-1">
          <p className="font-medium">Expected format (column headers can vary, we'll auto-detect):</p>
          <code className="block bg-muted p-2 rounded break-words">first_name, last_name, email, phone, source, notes, ...</code>
          <p>Required: at least <strong>first_name</strong> or <strong>last_name</strong>, and <strong>email</strong> or <strong>phone</strong>.</p>
        </div>
      </div>
    );
    footer = (
      <div className="flex items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={handleClose}>Cancel</Button>
          <Button type="button" size="sm" className="h-9" onClick={() => fileInputRef.current?.click()} data-testid="csv-choose-file-btn">
            <Upload className="w-4 h-4" /> Choose file
          </Button>
        </div>
      </div>
    );
  } else if (step === 'map') {
    body = (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 text-sm flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <FileSpreadsheet className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium truncate">{fileName}</span>
            <Badge variant="secondary">{rows.length} rows</Badge>
            <Badge variant="outline" className="text-green-700 border-green-300">{mappedColumns}/{headers.length} columns mapped</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={reset} title="Choose another file"><X className="w-4 h-4" /></Button>
        </div>
        <p className="field-hint">Confirm or change the field mapping. Columns marked "Skip" will be ignored.</p>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2 font-medium">CSV Column</th>
                <th className="text-left p-2 font-medium">Sample Value</th>
                <th className="text-left p-2 font-medium">Map to Lead Field</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {headers.map((h, idx) => (
                <tr key={idx}>
                  <td className="p-2 font-medium">{h || `Column ${idx + 1}`}</td>
                  <td className="p-2 text-xs text-muted-foreground truncate max-w-[140px]">{rows[0]?.[idx] || '—'}</td>
                  <td className="p-2">
                    <Select value={mapping[idx] || '__skip__'} onValueChange={(v) => updateMapping(idx, v)}>
                      <SelectTrigger className="h-8 text-xs w-full" data-testid={`csv-map-${idx}`}>
                        <SelectValue placeholder="Skip" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__skip__">— Skip this column —</SelectItem>
                        {LEAD_FIELDS.map(f => (
                          <SelectItem key={f.key} value={f.key}>
                            {f.label}{f.required ? ' *' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
    footer = (
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" className="h-9" onClick={reset}>Back</Button>
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={handleClose}>Cancel</Button>
          <Button type="button" size="sm" className="h-9" onClick={handlePreview} data-testid="csv-preview-btn">Preview Import</Button>
        </div>
      </div>
    );
  } else if (step === 'preview') {
    body = (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Label className="text-sm">Preview (first 5 of {rows.length} rows)</Label>
          <Badge variant="outline">{rows.length} leads will be imported</Badge>
        </div>
        <div className="border rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                {mappedFields.map(f => (
                  <th key={f.key} className="text-left p-2 font-medium whitespace-nowrap">{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {previewLeads.map((lead, i) => (
                <tr key={i}>
                  {mappedFields.map(f => (
                    <td key={f.key} className="p-2 whitespace-nowrap">{lead[f.key] || <span className="text-muted-foreground">—</span>}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length > 500 && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded text-xs">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p>This file has {rows.length} rows but the limit is 500 per import. Please split the file.</p>
          </div>
        )}
      </div>
    );
    footer = (
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" className="h-9" onClick={() => setStep('map')}>Back to Mapping</Button>
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={handleClose}>Cancel</Button>
          <Button type="button" size="sm" className="h-9" onClick={handleImport} disabled={importing || rows.length > 500} data-testid="csv-import-confirm-btn">
            {importing ? 'Importing...' : `Import ${rows.length} Leads`}
          </Button>
        </div>
      </div>
    );
  } else if (step === 'done' && result) {
    body = (
      <div className="space-y-4 text-center py-6">
        <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
        <div>
          <h3 className="text-lg font-bold">Import Complete</h3>
          <p className="text-muted-foreground">{result.message}</p>
        </div>
      </div>
    );
    footer = (
      <div className="flex items-center gap-2">
        <div className="ml-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="h-9" onClick={reset} data-testid="csv-import-another-btn">Import another file</Button>
          <Button type="button" size="sm" className="h-9" onClick={handleClose} data-testid="csv-import-done-btn">Done</Button>
        </div>
      </div>
    );
  }

  return {
    open: !!open,
    step,
    close: handleClose,
    title: 'Import leads from CSV',
    icon: FileSpreadsheet,
    headerBadge: <Badge variant="outline" className="text-xs capitalize">{step}</Badge>,
    body,
    footer,
  };
}

export default useCSVImportPane;
